'use client';

import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import { useContext } from 'react';

interface CycleTipProps {
  show: boolean;
  onClose: () => void;
  afterpayEnable: boolean;
  klarnaCycle: number;
}

// 提取公共内容组件
function CycleTipContent({
  afterpayEnable,
  klarnaCycle,
  isWAP = false,
}: {
  afterpayEnable: boolean;
  klarnaCycle: number;
  isWAP?: boolean;
}) {
  const t = useTranslations('CycleMsg');
  // 提取支付方式文本逻辑
  const getPaymentMethodText = () => {
    if (afterpayEnable && klarnaCycle) {
      return t('instructions.paymentOptions.afterpayOrKlarna');
    }
    if (afterpayEnable) {
      return t('instructions.paymentOptions.afterpay');
    }
    return t('instructions.paymentOptions.klarna');
  };

  return (
    <>
      {/* 副标题 */}
      <div className={isWAP ? 'text-[14px] font-bs mb-[12px]' : 'text-[16px] font-bs mb-[16px]'}>
        {t('howItWorks')}
      </div>

      {/* 内容列表 */}
      <ul
        className={`${isWAP ? 'space-y-3 text-[12px] leading-[18px] mb-[20px]' : 'text-[14px] leading-[22px]'} font-ir`}
      >
        <li className={isWAP ? 'flex items-start mb-[16px]' : 'flex items-center mb-[16px]'}>
          {t('selectPaymentMethod', { paymentMethod: getPaymentMethodText() })}
        </li>

        {afterpayEnable && (
          <li className={isWAP ? 'flex items-start mb-[16px]' : 'flex items-center mb-[16px]'}>
            {t('payIn4Instalments')}
            <Image
              src="/goods-detail/afterpay.png"
              alt={t('altText.zeeloolGlass')}
              width={isWAP ? 58 : 96}
              height={isWAP ? 24 : 30}
              className={isWAP ? 'ml-[8px] w-auto h-[24px]' : 'ml-[10px] w-auto h-[30px]'}
            />
          </li>
        )}
        {klarnaCycle > 0 && (
          <li className={isWAP ? 'flex items-start mb-[16px]' : 'flex items-center mb-[16px]'}>
            {t('payInKlarnaInstalments', { klarnaCycle })}
            <Image
              src="/goods-detail/klarna.png"
              alt={t('altText.zeeloolGlass')}
              width={isWAP ? 58 : 96}
              height={isWAP ? 24 : 30}
              className={isWAP ? 'ml-[8px] w-auto h-[24px]' : 'ml-[10px] w-auto h-[30px]'}
            />
          </li>
        )}

        <li className={isWAP ? 'flex items-start' : 'flex items-center'}>
          {t('over18AndCriteria')}
        </li>
      </ul>
    </>
  );
}

export default function CycleTip({ show, onClose, afterpayEnable, klarnaCycle }: CycleTipProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isWAP = deviceType === EPlatform.WAP;
  const t = useTranslations('CycleMsg');
  // PC端Dialog
  if (!isWAP) {
    return (
      <Dialog
        open={show}
        onClose={onClose}
        footer={null}
        title={t('shopNowPayLater')}
        sx={{
          '& .MuiDialog-paper': {
            width: '520px',
            maxWidth: '100%',
            borderRadius: '8px',
          },
          '& .MuiDialogTitle-root': {
            padding: '24px 40px',
            borderBottom: '1px solid #D4D4D4',
          },
        }}
      >
        <div className="w-full pt-[20px]">
          <CycleTipContent
            afterpayEnable={afterpayEnable}
            klarnaCycle={klarnaCycle}
            isWAP={false}
          />
        </div>
      </Dialog>
    );
  }

  // 移动端Popup
  return (
    <Popup open={show} onClose={onClose} title={t('shopNowPayLater')}>
      <div className="pb-40">
        <CycleTipContent afterpayEnable={afterpayEnable} klarnaCycle={klarnaCycle} isWAP />
      </div>
    </Popup>
  );
}
